// X4 — the concept x document incidence grid. PRD 16 §5 B2 specifies this
// drawing in words: *"a concept <-> document bipartite view built from
// `member_refs`"*, next to the ranked bars `ImpactRadius` already draws.
//
// SAME PAYLOAD, NO SECOND LOADER. `RadiusRow.doc_ids` IS the incidence row.
// The only thing the ranked view discards is the `:tNN` half of each
// `member_ref`, which the matrix needs to reach the per-doc term behind a
// cell — so `member_refs` was added to the row rather than a second endpoint.
//
// **Never split a `member_ref` and rejoin on its tail.** `term_id` is
// document-local: `term_metrics` holds twelve distinct ids covering 217
// distinct `(doc_id, term_id)` pairs, so a join keyed on the tail alone
// collapses 217 cells into 12. The composite string is already the key.
//
// THE FILL RATE IS LOAD-BEARING COPY. Measured at roughly 3% on a real corpus.
// An unstated sparse grid reads as "no data"; stated, it is the finding —
// most concepts are local to one document and a few span everything, which is
// exactly what a change-control conversation is about.
//
// Column order is LAYOUT, not arithmetic: the backend ranks the rows, and
// sorting documents by how many concepts touch them pulls the mass into one
// corner instead of scattering it. Nothing here recomputes a value.

import { Card, EmptyState } from "../../ui";
import { Matrix } from "../../charts";
import type { ImpactRadiusReport } from "./api";
import { shownOf } from "./format";
import {
  MATRIX_EMPTY_HEADLINE,
  MATRIX_SUBTITLE,
  MATRIX_TITLE,
  RADIUS_UNRECONCILED_BODY,
  RADIUS_UNRECONCILED_HEADLINE,
  matrixFill,
} from "./metricLabels";
import "./analytics.css";

const MAX_ROWS = 40;
const MAX_COLUMNS = 40;

export type MatrixShape = {
  rows: string[];
  columns: string[];
  filled: Set<string>;
  cells: number;
  rowsTotal: number;
  columnsTotal: number;
};

/** Radius rows -> a grid. Exported for the test: the ordering rule and the
 *  cell count are the contract, and asserting them through rendered `<rect>`s
 *  would pin the drawing instead. */
export function matrixShape(report: ImpactRadiusReport): MatrixShape {
  // Documents ranked by how many concepts touch them, so the dense corner is
  // top-left rather than scattered across an empty field.
  const weight = new Map<string, number>();
  for (const row of report.rows) {
    for (const docId of row.doc_ids) {
      weight.set(docId, (weight.get(docId) ?? 0) + 1);
    }
  }
  const columnsAll = [...weight.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([docId]) => docId);

  // Rows arrive already ranked by blast radius; do not re-sort.
  const rowsAll = report.rows;
  const rows = rowsAll.slice(0, MAX_ROWS);
  const columns = columnsAll.slice(0, MAX_COLUMNS);
  const columnIndex = new Map(columns.map((docId, i) => [docId, i]));

  const filled = new Set<string>();
  rows.forEach((row, r) => {
    for (const docId of row.doc_ids) {
      const c = columnIndex.get(docId);
      if (c !== undefined) filled.add(`${r}:${c}`);
    }
  });

  return {
    rows: rows.map((row) => row.term),
    columns,
    filled,
    cells: filled.size,
    rowsTotal: rowsAll.length,
    columnsTotal: columnsAll.length,
  };
}

export function VocabularyMatrix({ report }: { report: ImpactRadiusReport }) {
  // "Pass 2 has never run" and "no concept spans two documents" are opposite
  // claims and only the second is a reason to publish another document.
  if (!report.reconciled) {
    return (
      <Card header={MATRIX_TITLE}>
        <EmptyState
          headline={RADIUS_UNRECONCILED_HEADLINE}
          body={RADIUS_UNRECONCILED_BODY}
        />
      </Card>
    );
  }

  const shape = matrixShape(report);

  if (shape.rows.length === 0) {
    return (
      <Card header={MATRIX_TITLE}>
        <EmptyState headline={MATRIX_EMPTY_HEADLINE} />
      </Card>
    );
  }

  return (
    <Card header={MATRIX_TITLE}>
      <p className="type-bodyS maisight-analytics-muted">{MATRIX_SUBTITLE}</p>
      <Matrix
        label={
          `Concept by document grid: ${shape.rows.length} concepts across` +
          ` ${shape.columns.length} documents, ${shape.cells} pairings marked`
        }
        rows={shape.rows}
        columns={shape.columns}
        filled={shape.filled}
        fill={matrixFill(shape.cells, shape.rows.length, shape.columns.length)}
      />
      <div className="maisight-analytics-card-footer type-bodyS">
        <span>{shownOf(shape.rows.length, shape.rowsTotal, "concept")}</span>
        <span>{shownOf(shape.columns.length, shape.columnsTotal, "document")}</span>
      </div>
    </Card>
  );
}
